export default {
  nav: {
    home: 'Home',
    about: 'About',
    generation: 'Generation',
    optimization: 'Optimization',
    blog: 'Blog',
    github: 'GitHub'
  },
  hero: {
    title: 'KernelPilot',
    subtitle: 'Pilot for Automatic, Fast, and Efficient Parallel Code Generation and Optimization',
    description: 'Transform your natural language prompt or sequential code into highly optimized parallel implementations. Leverage AI-powered analysis and code generation for CUDA, OpenMP, and other parallel computing frameworks.',
    cta: {
      generation: 'Generation',
      optimization: 'Optimization',
      secondary: 'Introduction'
    }
  },
  introduction: {
    title: 'Introduction',
    subtitle: 'Revolutionizing Parallel Development with Speed, Automation & Efficiency',
    description: 'Kernel Pilot is a cutting-edge online platform designed to dramatically simplify and accelerate the creation of high-performance parallel implementations. It automates the generation of optimized code for CUDA, OpenMP, and other parallel computing frameworks, combining rapid turnaround times with deep hardware-aware tuning.',
    items: {
      autoGen: {
        title: 'Fully Automatic Generation',
        description: 'Users describe their computational problem—matrix multiplication, convolution, reduction, or custom parallel workloads. KernelPilot instantly produces the necessary parallel code. No manual tuning required.'
      },
      optimization: {
        title: 'Blazing Fast Performance',
        description: 'Each generated kernel is fine-tuned to match or exceed typical handwritten implementations. The platform optimizes thread and block configurations, memory access patterns, shared memory usage, register allocation, and even tensor core operations.'
      },
      multiPlatform: {
        title: 'Efficiency First',
        description: 'The code produced by KernelPilot is lean and streamlined—designed to maximize occupancy, minimize thread divergence, and minimize memory bottlenecks. The result: kernels that run faster and more consistently than typical baseline code.'
      },
      analysis: {
        title: 'Ideal for Engineers & Researchers',
        description: 'Whether you\'re a GPU researcher, AI developer, or system integrator, KernelPilot streamlines the workflow. You avoid boilerplate parallel setup and instead focus on algorithmic design and performance goals.'
      }
    }
  },
  stats: {
    codeGenerated: 'Lines of Code Generated',
    optimizations: 'Optimizations Applied',
    speedup: 'Average Speedup',
    users: 'Active Users'
  },
  about: {
    title: 'About KernelPilot',
    description: 'KernelPilot is an advanced AI-powered platform designed to assist developers in creating and optimizing parallel code. Our mission is to make parallel programming accessible to everyone, from students learning parallel computing to experienced engineers working on high-performance computing applications.',
    mission: {
      title: 'Our Mission',
      content: 'To democratize parallel computing by providing intelligent tools that bridge the gap between sequential and parallel programming paradigms.'
    },
    vision: {
      title: 'Our Vision',
      content: 'A world where every developer can harness the power of parallel computing without the steep learning curve.'
    }
  },
  versions: {
    title: 'News & Updates',
    subtitle: 'Stay informed about our latest developments and announcements',
    readMore: 'Read More',
    categories: {
      all: 'All',
      release: 'Release',
      feature: 'Feature',
      research: 'Research',
      tutorial: 'Tutorial'
    }
  },
  generation: {
    title: 'Code Generation',
    subtitle: 'Generate optimized parallel code from natural language descriptions',
    inputPromptPlaceholder: 'Enter your natural language prompt here...',
    outputPlaceholder: 'Generated parallel code will appear here...',
    buttons: {
      run: 'Generate'
    },
    options: {
      target_platform: 'Target Platform',
      target_hardware: 'Target Hardware',
      optimization: 'Optimization Level'
    },
    labels: {
      inputPrompt: 'Input Prompt',
      outputCode: 'Output Code'
    },
    status: {
      Ready: 'Ready',
      Success: 'Success',
      'Pre-processing': 'Pre-processing',
      Generating: 'Generating',
      Optimizing: 'Optimizing',
      Profiling: 'Profiling',
      Fixing: 'Fixing',
      Finished: 'Finished',
      Error: 'Error'
    },
    results: {
      speedup: 'Estimated Speedup'
    }
  },
  optimization: {
    title: 'Code Optimization',
    subtitle: 'Optimize your existing parallel code for better performance',
    inputCodePlaceholder: 'Enter your sequential code here...',
    inputTestDataPlaceholder: `Enter your test data format as follows:
[Number of test cases]
[Number of variables]

[Variable 1 Name]
[Variable 1 Data]

[Variable 2 Name]
[Variable 2 Data]`,
    outputPlaceholder: 'Optimized parallel code will appear here...',
    buttons: {
      run: 'Optimize'
    },
    options: {
      target_platform: 'Target Platform',
      target_hardware: 'Target Hardware',
      optimization: 'Optimization Level'
    },
    labels: {
      inputCode: 'Input Code',
      inputTestData: 'Input Test Data',
      outputCode: 'Output Code'
    },
    status: {
      Ready: 'Ready',
      Success: 'Success',
      'Pre-processing': 'Pre-processing',
      Generating: 'Generating',
      Optimizing: 'Optimizing',
      Profiling: 'Profiling',
      Fixing: 'Fixing',
      Finished: 'Finished',
      Error: 'Error'
    },
    results: {
      speedup: 'Estimated Speedup'
    }
  },
  footer: {
    tagline: 'Pilot for Automatic, Fast, and Efficient Parallel Code Generation',
    copyright: '© 2025 Kernel Pilot Team. All rights reserved.',
    quickLinks: 'Quick Links',
    connect: 'Connect',
    links: {
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      contact: 'Contact Us'
    }
  },
  common: {
    loading: 'Loading...',
    error: 'An error occurred',
    retry: 'Retry',
    back: 'Back',
    next: 'Next',
    submit: 'Submit',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    view: 'View',
    search: 'Search',
    filter: 'Filter',
    sort: 'Sort',
    language: 'Language'
  },
  contact: {
    suggestions: {
      title: 'Suggestions'
    },
    collaboration: {
      title: 'Academic & Industry Collaborations'
    }
  }
}
